import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  TrendingUp, 
  Users, 
  Leaf, 
  Plus, 
  Eye, 
  CheckCircle, 
  Clock, 
  AlertTriangle,
  FileCheck,
  CreditCard,
  Award,
  Settings,
  Bell
} from 'lucide-react';
import api from '../../utils/api';

// Import existing components
import AdminDashboard from '../admin/AdminDashboard';
import VerifierDashboard from '../verifier/VerifierDashboard';
import ProjectVerificationDashboard from '../verification/ProjectVerificationDashboard';
import MyProjects from '../marketplace/MyProjects';

const UnifiedDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Get user from authentication
  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Check if user is authenticated
        if (!api.utils.isAuthenticated()) {
          navigate('/auth');
          return;
        }

        // Get current user data
        const response = await api.auth.getCurrentUser();
        setUser(response.data.user);
      } catch (error) {
        console.error('Error fetching user:', error);
        // Clear invalid token and redirect to auth
        api.utils.removeToken();
        navigate('/auth');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [navigate]);

  // Show loading while fetching user
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Redirect if no user
  if (!user) {
    return null;
  }

  const getDashboardConfig = () => {
    switch (user.role) {
      case 'admin':
        return {
          title: 'Admin Dashboard',
          subtitle: 'Platform Management & Oversight',
          primaryColor: 'blue',
          tabs: [
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'applications', label: 'Applications', icon: FileCheck },
            { id: 'users', label: 'User Management', icon: Users },
            { id: 'settings', label: 'Platform Settings', icon: Settings }
          ],
          quickActions: [
            { label: 'Review Applications', path: '/admin/applications', icon: FileCheck, color: 'blue' },
            { label: 'Platform Analytics', path: '/admin/analytics', icon: BarChart3, color: 'green' },
            { label: 'User Management', path: '/admin/users', icon: Users, color: 'purple' },
            { label: 'System Settings', path: '/admin/settings', icon: Settings, color: 'gray' }
          ]
        };
      
      case 'verifier':
        return {
          title: 'Verifier Dashboard',
          subtitle: 'Project Verification & Quality Assurance',
          primaryColor: 'purple',
          tabs: [
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'projects', label: 'Active Projects', icon: Leaf },
            { id: 'marketplace', label: 'Find Projects', icon: Eye },
            { id: 'profile', label: 'Profile', icon: Award }
          ],
          quickActions: [
            { label: 'Browse Projects', path: '/verification/marketplace', icon: Eye, color: 'blue' },
            { label: 'Submit Proposal', path: '/verification/requests', icon: Plus, color: 'green' },
            { label: 'Workspace', path: '/verification/workspace', icon: FileCheck, color: 'purple' },
            { label: 'My Profile', path: '/verifier/profile', icon: Award, color: 'orange' }
          ]
        };
      
      case 'buyer':
        return {
          title: 'Buyer Dashboard',
          subtitle: 'Carbon Credit Portfolio & Purchases',
          primaryColor: 'green',
          tabs: [
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'portfolio', label: 'My Credits', icon: CreditCard },
            { id: 'marketplace', label: 'Browse Credits', icon: Eye },
            { id: 'impact', label: 'Impact Report', icon: Leaf }
          ],
          quickActions: [
            { label: 'Browse Marketplace', path: '/marketplace', icon: Eye, color: 'blue' },
            { label: 'Purchase Credits', path: '/marketplace/purchase', icon: CreditCard, color: 'green' },
            { label: 'My Certificates', path: '/marketplace/my-projects', icon: Award, color: 'purple' },
            { label: 'Impact Report', path: '/impact', icon: Leaf, color: 'emerald' }
          ]
        };
      
      default: // project_owner
        return {
          title: 'Project Owner Dashboard',
          subtitle: 'Manage Your Carbon Credit Projects',
          primaryColor: 'emerald',
          tabs: [
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'projects', label: 'My Projects', icon: Leaf },
            { id: 'verification', label: 'Verification', icon: CheckCircle },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp }
          ],
          quickActions: [
            { label: 'Create Project', path: '/marketplace/create', icon: Plus, color: 'emerald' },
            { label: 'View Projects', path: '/marketplace/my-projects', icon: Leaf, color: 'blue' },
            { label: 'Verification Status', path: '/verification/dashboard', icon: CheckCircle, color: 'purple' },
            { label: 'Request Verification', path: '/verification/requests', icon: FileCheck, color: 'orange' }
          ]
        };
    }
  };

  const config = getDashboardConfig();

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      return <OverviewTab config={config} user={user} />;
    }

    // Render existing specialized dashboards for specific tabs
    switch (user.role) {
      case 'admin':
        if (activeTab === 'applications') return <AdminDashboard />;
        break;
      case 'verifier':
        if (activeTab === 'projects') return <VerifierDashboard />;
        break;
      case 'project_owner':
        if (activeTab === 'projects') return <MyProjects />;
        if (activeTab === 'verification') return <ProjectVerificationDashboard />;
        break;
    }

    // Default fallback content
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{config.tabs.find(tab => tab.id === activeTab)?.label}</h3>
        <p className="text-gray-600">Content for this section is coming soon.</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{config.title}</h1>
              <p className="text-gray-600 mt-1">{config.subtitle}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-500 relative">
                <Bell className="h-6 w-6" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white"></span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-medium">{user.name.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{user.role.replace('_', ' ')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {config.quickActions.map((action, index) => (
            <button
              key={index}
              onClick={() => navigate(action.path)}
              className={`bg-white p-6 rounded-lg shadow hover:shadow-md transition-shadow border-l-4 border-${action.color}-500`}
            >
              <div className="flex items-center">
                <action.icon className={`h-8 w-8 text-${action.color}-600 mr-4`} />
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-900">{action.label}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            {config.tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? `border-${config.primaryColor}-500 text-${config.primaryColor}-600`
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-5 w-5 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {renderTabContent()}
      </div>
    </div>
  );
};

// Overview tab component
const OverviewTab = ({ config, user }) => {
  const [stats, setStats] = useState({});

  useEffect(() => {
    // Mock stats based on role
    const mockStats = {
      admin: {
        'Total Users': '1,234',
        'Active Projects': '89',
        'Pending Applications': '12',
        'Platform Revenue': '$45,678'
      },
      verifier: {
        'Active Verifications': '8',
        'Completed Projects': '23',
        'Success Rate': '96%',
        'Earnings This Month': '$3,450'
      },
      buyer: {
        'Credits Owned': '1,500',
        'Total Spent': '$15,000',
        'CO2 Offset': '45 tons',
        'Active Certificates': '12'
      },
      project_owner: {
        'Active Projects': '3',
        'Total Credits': '2,500',
        'Revenue': '$25,000',
        'Verification Status': '2 Verified'
      }
    };

    setStats(mockStats[user.role] || mockStats.project_owner);
  }, [user.role]);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {Object.entries(stats).map(([key, value], index) => (
          <div key={index} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`w-8 h-8 bg-${config.primaryColor}-100 rounded-full flex items-center justify-center`}>
                    <BarChart3 className={`h-5 w-5 text-${config.primaryColor}-600`} />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{key}</dt>
                    <dd className="text-lg font-medium text-gray-900">{value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
        </div>
        <div className="px-6 py-4">
          <div className="space-y-4">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className={`w-2 h-2 bg-${config.primaryColor}-500 rounded-full`}></div>
                <p className="text-sm text-gray-600">
                  Sample activity item {i + 1} for {user.role.replace('_', ' ')}
                </p>
                <span className="text-xs text-gray-400">{i + 1}h ago</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedDashboard; 